/* packages/ui/react/jurispicker.css — THE JURISDICTION PICKER'S ONE STYLESHEET.
 *
 * ══ 🔴 RUN RK — CONSOLIDATED, ON ADAM'S RULING. TWO COPIES WERE THE ROOT CAUSE. ═════════════════
 * Until this file existed there were two: `apps/main/client/src/screens/Clark/jurispicker.css` and
 * `apps/steel/client/src/styles/jurispicker.css`, each ~400 lines, neither importing the other, and
 * every fix had to be made twice. It repeatedly was not:
 *   · RUN PK — Clark lost the "Set your home state in settings" link; Research kept it, live.
 *   · RUN RJ — Clark got `overscroll-behavior`, `touch-action` and `-webkit-overflow-scrolling` on
 *     the list and an un-clipped fixed ancestor; RESEARCH GOT NONE OF THEM, and Adam's phone found
 *     the difference. Research was ALSO the only copy whose sheet sat under its own scrim.
 * Three consecutive runs paid for the duplication. This is the fix for the cause.
 *
 * 🔴 WHAT WAS MEASURED BEFORE MERGING, because "identical apart from names" is a claim, not a fact.
 * Both files were stripped of comments, had every `var(--token, raw-fallback)` reduced to the token,
 * and had apps/main's vocabulary mapped onto Steel's. Result: **215 of Steel's 244 declarations were
 * already identical**, and the remainder split into
 *   · APP-SPECIFIC SELECTORS that cannot collide — Clark's `.ccv2-clark-jurisdock` / `-jurisbtn` /
 *     `-jurisscrim`, and Steel's `.jp[data-anchored]*`. BOTH FILES ALREADY CARRIED THE OTHER'S:
 *     Steel's copy has shipped `.ccv2-clark-jurisdock .jp` rules for its whole life. The union is
 *     therefore very close to a no-op for both apps, which is why this merge is safe.
 *   · TEN TRUE DRIFTS, resolved one at a time below, each marked `RUN RK · DRIFT n` with the reason.
 *
 * 🔴 THE TOKEN VOCABULARIES ARE TWO NAMES FOR ONE PALETTE, AND THAT WAS MEASURED TOO, on the live
 * root of BOTH apps: `--cc-ink`/`--cc-text-primary` #141417 · `--cc-card`/`--cc-bg-card` #FFFFFF ·
 * `--cc-card-alt`/`--cc-bg-subtle` #F7F6F1 · `--cc-hairline`/`--cc-border-soft` #ceccc5 ·
 * `--cc-accent-fill`/`--cc-brand` #003A8F · `--cc-on-accent`/`--cc-brand-on-brand` #FFFFFF ·
 * `--cc-edge`/`--cc-border-strong` #87877f — every pair identical, in BOTH apps.
 * ⚠️ ONE PAIR IS NOT A PAIR: `--cc-ink-muted` is #63625B and maps to `--cc-text-muted` (#63625B),
 * NOT to `--cc-text-secondary` (#55554F). That single mismatch is the entire reason Clark's preset
 * pills measured rgb(99,98,91) and Research's rgb(85,85,79). See DRIFT 3/6/8/9.
 *
 * 🔴 NO RAW-COLOUR FALLBACKS. Steel's copy carried `var(--token, #hex)` throughout; every one of
 * those tokens is defined in both apps (measured, above), so the fallbacks were dead weight AND a
 * raw colour apps/main's rawcolor ratchet is right to reject. They are gone.
 *
 * Consumers: apps/main Clark composer + the home-page clark-widget, and apps/steel's
 * JurisdictionChip. All three import `@citeclerk/ui/JurisdictionPicker`, which imports this file.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════ */

/* apps/steel/client/src/styles/jurispicker.css — the pop-from-pill jurisdiction picker.
   Design language from the approved mockup, rebuilt on Steel's real --cc-* tokens (ultramarine = the only
   accent; premium hairlines + soft elevation + serif header). Theme-aware via the tokens. */

.jp {
  position: fixed; bottom: 84px; left: 50%; transform: translateX(-50%);
  width: min(880px, 94vw); max-height: min(84vh, 620px);
  background: var(--cc-bg-card);
  border: 1px solid var(--cc-border-soft);
  border-radius: 18px;
  box-shadow: var(--cc-shadow-dropdown);
  /* ⭐ RUN UF · UF-1 — `visible`, AND THE CLIP THAT WAS HERE IS NOT LOST, IT IS MOVED TO ITS ONE
     REAL OWNER. `overflow: hidden` was credited with two jobs — the 18px corners and containing the
     scroll. MEASURED, it was doing one: `.jp__cols` below has carried `overflow: auto` since this
     file was written, so the scroll was always contained a level down, and writing `hidden` onto
     `.jp__cols` would not have moved a clip, it would have DELETED that scroller. What this rule
     was really holding is `.jp__foot`'s fill, the only descendant whose background reaches a
     rounded corner — which is why the foot now rounds itself. See `.jp__foot` below.
     🔴 AND WHAT IT WAS ALSO DOING WAS EATING THE POINTER. `.jp::after` is positioned OUTSIDE this
     box (`bottom:-7px` / `top:-7px`), so for the whole of this arc the shape the design calls a
     pointer has been a NOTCH inside the sheet's edge: 0.0px protrusion, 19/19 themes, on both
     callers and in both directions (ops/run-uf-pointer). It now protrudes 8px opening up and 9px
     opening down, and the anchoring does not move by a hundredth of a pixel. */
  z-index: 60; overflow: visible; display: flex; flex-direction: column;
  animation: jp-in .18s ease;
}
@keyframes jp-in { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
@media (prefers-reduced-motion: reduce) { .jp { animation: none; } }
/* the pointer anchoring it to the pill */
.jp::after {
  content: ""; position: absolute; bottom: -7px; left: 50%; transform: translateX(-50%) rotate(45deg);
  width: 14px; height: 14px; background: var(--cc-bg-card);
  border-right: 1px solid var(--cc-border-soft); border-bottom: 1px solid var(--cc-border-soft);
}

/* ══ 🔴 RUN RH · RH-2 — ANCHORED TO THE COMPOSER INSTEAD OF THE PILL. ══════════════════════════
   `[data-anchored]` is set only when the caller passes an `anchorRef` (JurisdictionPicker.jsx), so
   every rule above is the pill's behaviour, unchanged, for any caller that does not.
   · z-index — the pill's copy lives INSIDE `.st-pill`'s stacking context, where 60 is above
     everything local. Rendered as a sibling of the page it has to beat the pill's own 400 outright,
     or the sheet opens behind the strip it was moved off.
   · the pointer flips — pointing DOWN at a chip below it when the sheet opened upward, and UP at a
     chip above it when it opened downward. A pointer that does not follow the sheet is worse than
     no pointer: it asserts an anchor.
   · `top`/`bottom`/`max-height` come from the element's inline style, measured against the real
     viewport at open. Nothing is restated here as a number. */
.jp[data-anchored] { z-index: 500; }
/* ══ 🔴 RUN SF · SF-1 — THE POINTER'S X, AND WHY IT IS THE ONLY THING THAT MOVES. ═══════════════
   `.jp::after` above is `left: 50%` OF THE SHEET, which on a centred sheet is the centre of the
   viewport. RH's `anchorRef` resolves the SIDE the sheet opens on; it never resolved the X, because
   its only anchored caller was a centred chip under a centred composer and a missing rule looked
   exactly like a correct one. Mounted in the workspace's collapsed header the chip is at the right
   end of the row — measured at 1440x900, chip centre x≈1246, pointer still at x=719, aimed at the
   question text.
   `--jp-arrow-x` is published by JurisdictionPicker.jsx in the SHEET'S own coordinates, clamped so
   the pointer always has a straight edge to sit on. The fallback is the old value verbatim, so an
   unanchored caller — the pill — renders byte-identically and this rule cannot reach it. */
.jp[data-anchored]::after { left: var(--jp-arrow-x, 50%); }
.jp[data-anchored="down"]::after {
  bottom: auto; top: -7px;
  border-right: none; border-bottom: none;
  border-left: 1px solid var(--cc-border-soft); border-top: 1px solid var(--cc-border-soft);
}

.jp__head { padding: 12px 22px 10px; display: flex; align-items: center; gap: 14px; border-bottom: 1px solid var(--cc-border-soft); }
.jp__title { margin: 0; font-family: var(--cc-font-display, Georgia, serif); font-size: 19px; font-weight: 600; color: var(--cc-text-primary); letter-spacing: .01em; }
.jp__search {
  flex: 1; max-width: 300px; margin-left: auto; padding: 7px 12px;
  border: 1px solid var(--cc-border-soft); border-radius: 9px; font: inherit; font-size: 13px;
  background: var(--cc-bg-subtle); color: var(--cc-text-primary);
}
.jp__search:focus { outline: 2px solid var(--cc-brand-a20); background: var(--cc-bg-card); }

.jp__presets {
  display: flex; gap: 7px; padding: 9px 22px 3px;
  /* RUN PK — centred on the card (padding equal both sides), per Adam's ruling; see Clark's copy
     of this rule for the measurements. Pills keep their own widths and gap. */
  justify-content: center;
}
/* RUN RK · DRIFT 6 — Steel's `--cc-text-secondary` (#55554F) wins over Clark's `--cc-ink-muted`
   (#63625B). Both are legible; the darker is the higher-contrast of the two and this is the label
   of an interactive control. Clark's pills move from rgb(99,98,91) to rgb(85,85,79). */
.jp__preset {
  padding: 5px 14px; border-radius: 999px; border: 1px solid var(--cc-border-soft);
  background: var(--cc-bg-subtle); font: inherit; font-size: 12.5px; font-weight: 600;
  color: var(--cc-text-secondary); cursor: pointer; transition: all .12s ease;
}
.jp__preset:hover { border-color: var(--cc-brand); color: var(--cc-brand); }
.jp__preset.is-on { background: var(--cc-brand); border-color: var(--cc-brand); color: var(--cc-brand-on-brand); }
/* "My state" before a home state is set — a quiet prompt, not a fake state; disabled until set in settings. */
.jp__preset--prompt { color: var(--cc-text-muted); border-style: dashed; cursor: default; background: transparent; }
.jp__preset--prompt:hover { border-color: var(--cc-border-soft); color: var(--cc-text-muted); }

.jp__pills { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 22px 2px; }
.jp__pill {
  display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 12px; font-weight: 600;
  /* RUN RK · DRIFT 4 — Clark's SOLID border wins, and it is not a preference: RUN CF's note at the
     head of that file holds a border that IDENTIFIES A CONTROL to 3:1, and Steel's
     `--cc-brand-border` is rgba(0,58,143,.25) — a quarter-opacity wash over a 6% tint, nowhere near
     it. This pill is a control (it removes a jurisdiction when clicked).
     RUN RK · DRIFT 5 — `--cc-chip-radius` resolves to 999px in BOTH apps, so the token and Steel's
     literal are the same pixel; the token is kept because it is the one that can be re-themed. */
  background: var(--cc-brand-a06); color: var(--cc-brand);
  border: 1px solid var(--cc-brand); border-radius: var(--cc-chip-radius); padding: 2.5px 10px; cursor: pointer;
}
.jp__pill:hover { background: var(--cc-brand-a10); }
.jp__pill-x { opacity: .55; font-size: 10px; }
.jp__more { font-size: 12px; color: var(--cc-text-muted); align-self: center; }

.jp__cols { display: grid; grid-template-columns: 244px 1fr; gap: 0; overflow: auto; }
.jp__col { padding: 10px 18px 12px; }
.jp__col--fed { border-right: 1px solid var(--cc-border-soft); }
.jp__eyebrow { margin: 2px 0 8px; font-size: 10px; letter-spacing: .13em; text-transform: uppercase; color: var(--cc-text-muted); font-weight: 700; }
.jp__eyebrow--gap { margin-top: 12px; }

.jp__row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; padding: 1.5px 4px; border-radius: 6px; color: var(--cc-text-primary); }
.jp__row:hover { background: var(--cc-brand-a04); }
/* RUN RK · DRIFT 8 — as DRIFT 6: the darker of the two muted roles. */
.jp__row--small { font-size: 12px; color: var(--cc-text-secondary); padding: 2px 4px; }
/* RUN RK · DRIFT 7 — `--cc-accent-fill` and `--cc-brand` BOTH resolve to #003A8F in both apps, so
   this drift was a name and not a colour. Steel's spelling kept. */
.jp__row input[type=checkbox], .jp label input[type=checkbox] { accent-color: var(--cc-accent-fill); width: 14px; height: 14px; cursor: pointer; flex: none; }
.jp__rowname { cursor: pointer; flex: 1; }
.jp__exp { margin-left: auto; font-size: 10px; color: var(--cc-text-muted); cursor: pointer; padding: 2px 6px; border-radius: 4px; background: none; border: none; }
.jp__exp:hover { color: var(--cc-brand); background: var(--cc-brand-a06); }
.jp__sub { margin: 1px 0 4px 22px; }
/* RUN RK · DRIFT 9 — as DRIFT 6. */
.jp__sub .jp__row, .jp__sub label { font-size: 12px; color: var(--cc-text-secondary); }

/* compact multi-column state grid — everything visible, no scroll */
.jp__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0 10px; align-content: start; }
.jp__gcell { break-inside: avoid; }
.jp__grid .jp__row { font-size: 12.5px; padding: 2.5px 3px; }
.jp__grid .jp__sub { grid-column: 1 / -1; margin-left: 20px; }
.jp label.jp__row { cursor: pointer; }

/* ⭐ RUN UF · UF-1 — THE BOTTOM CORNERS, TAKEN BACK FROM THE PARENT'S CLIP. `.jp__foot` is the only
   child of `.jp` carrying a fill that reaches a rounded corner, and children paint after the
   parent's border, so with `.jp` no longer clipping, this background would square off the sheet's
   bottom two corners — measured at +72 foot-coloured pixels per corner, on all 19 themes and both
   callers. 17px is the parent's 18px radius less its 1px border: exactly where the parent was
   clipping it. Measured against the clipped rendering: the top corners are byte-identical and the
   bottom two differ in at most 21 pixels of 576, none by more than one channel step — the arc's
   antialiasing, not a moved arc. */
.jp__foot {
  display: flex; justify-content: space-between; align-items: center;
  border-top: 1px solid var(--cc-border-soft); padding: 11px 22px;
  background: var(--cc-bg-subtle);
  border-radius: 0 0 17px 17px;
}
.jp__count { font-size: 12.5px; color: var(--cc-text-muted); }
.jp__sep { margin: 0 8px; color: var(--cc-border-strong); }
.jp__note { font-style: italic; }
.jp__actions { display: flex; align-items: center; }
.jp__clear { background: none; border: none; color: var(--cc-text-muted); font: inherit; font-size: 12.5px; cursor: pointer; text-decoration: underline; margin-right: 14px; }
/* RUN RK · DRIFT 3 — Steel's wins for the same reason as DRIFT 2: `.jp__clear`'s resting colour
   IS `--cc-text-muted`, so Clark's hover repainted the identical value. `--cc-text-secondary`
   (#55554F) is a genuine darkening of #63625B. */
.jp__clear:hover { color: var(--cc-text-secondary); }
.jp__apply {
  background: var(--cc-brand); color: var(--cc-brand-on-brand); border: none; border-radius: 9px;
  padding: 8px 24px; font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer;
  /* RUN RK · DRIFT 1 — Clark's token wins over Steel's raw `0 1px 2px rgba(0,42,104,.3)`. Same
     role, and a raw colour in a file apps/main compiles is what the rawcolor ratchet exists to stop. */
  box-shadow: var(--cc-shadow-sm);
}
/* RUN RK · DRIFT 2 — Steel's wins, because Clark's was a no-op: it set `--cc-accent-fill`, which
   is the button's own resting background (#003A8F), so Clark's Apply had NO hover feedback at all.
   `--cc-brand-navy` (#002368) is defined in both apps and is a real darkening. */
.jp__apply:hover { background: var(--cc-brand-navy); }

/* the pill's jurisdiction button, tinted when a filter is active */
.st-pill__item--juris.is-filtered {
  background: var(--cc-brand-a12); color: var(--cc-brand);
  /* RUN RK · DRIFT 10 — as DRIFT 4: a control's identifying border, held to 3:1. */
  border: 1px solid var(--cc-brand);
}

@media (max-width: 720px) {
  .jp { width: 96vw; }
  .jp__cols { grid-template-columns: 1fr; }
  .jp__col--fed { border-right: none; border-bottom: 1px solid var(--cc-border-soft); }
  .jp__grid { grid-template-columns: repeat(2, 1fr); }
}


/* ══ CLARK-ONLY ADDITIONS — inert in Research, which mounts no `.ccv2-clark-*` node. ═════════════
   These exist because Clark's picker hangs off the COMPOSER rather than off Research's floating
   pill, and because Clark's composer sits at the bottom of a scrolling thread. Carried across
   VERBATIM: every token they use (`--cc-accent-fill`, `--cc-accent-text`, `--cc-card`,
   `--cc-card-hover`, `--cc-chip-radius`, `--cc-edge`, `--cc-focus-ring-color`, `--cc-hairline`,
   `--cc-ink`) was resolved on BOTH apps' live roots and is identical in each, so no remapping was
   needed and none was done. Their position in the cascade is unchanged too — after the base `.jp`
   rules they override, before the <=720px sheet block that re-pins both selectors. */
/* ── CLARK-ONLY ADDITIONS. Everything above is Steel's file verbatim; everything below exists
   because Clark's picker hangs off the COMPOSER rather than off Steel's floating pill, and
   because Clark's composer sits at the bottom of a scrolling thread. No rule above is
   overridden — these are new selectors only, so the shared design stays byte-identical. */

/* Clark's composer is not fixed to the viewport bottom the way Steel's pill is, so the popover
   anchors to the composer instead of to `bottom: 84px`. Same size, same chrome, same animation. */
.ccv2-clark-jurisdock { position: relative; }

/* 🔴🔴 BUNDLE V7 · LANE 5 — THE PANEL OPENS CENTRED. IT WAS LEFT-ALIGNED TO THE PILL AND RAN OFF
   THE SCREEN. Measured at 1440 (probe/picker-position.cjs): the panel is 880px wide and the pill
   is 129px, so anchoring `left: 0` to the pill put the panel at x=583 with its right edge at
   1463 — 23px PAST the viewport, with the whole panel 376px off the pill's centre.

   ⚠️ THE SHARED RULE WAS ALREADY RIGHT AND THIS OVERRODE IT. Steel's `.jp` declares
   `position: fixed; left: 50%; transform: translateX(-50%)`. The Clark block below it re-set
   `position: absolute; left: 0; transform: none` — so the computed style read `left: 0px;
   transform: none` while the file three rules up still said `left: 50%`, which is why reading
   either rule alone makes the layout look correct. The header above this block claims "No rule
   above is overridden — these are new selectors only"; that was true when it was written and
   stopped being true here.

   Anchoring stays on the dock (Clark's composer is not fixed to the viewport bottom the way
   Steel's pill is, which is the real reason this block exists), but it centres on it. */
.ccv2-clark-jurisdock .jp {
  position: absolute;
  /* 🔴 RUN AE · 2 — the anchor is unchanged; `--jp-drop` is the CLAMP. It is 0px unless the default
     placement would put the panel's top above the nav or its bottom past the fold, in which case
     Composer.jsx sets the smallest correction that satisfies both. See the block above its
     `useLayoutEffect` for the measurements (the zero state opened at top −141.5 at 1440×900) and
     for why the correction is written here rather than as a `transform`. Both fall back to their
     pre-clamp values, so a render that never runs the effect is the old behaviour, not a broken
     one — and ≤720px, where the panel is a pinned sheet, the effect writes nothing at all. */
  bottom: calc(100% + 12px - var(--jp-drop, 0px));
  left: 50%;
  transform: translateX(-50%);
  /* The pill is centred on the composer, so a panel centred on the pill is centred on the page.
     This is belt-and-braces for narrow viewports where the stage is offset by the sidebar. */
  max-width: calc(100vw - 24px);
  /* `--jp-cap` is the space between the nav and the fold. It only ever TIGHTENS the shared rule's
     `min(84vh, 620px)` — a third term in the same `min()`, never a replacement for it — so on a
     viewport with room the panel is exactly the size Research's is. */
  max-height: min(84vh, 620px, var(--jp-cap, 100vh));
}
/* 🔴 THE KEYFRAME MUST CARRY THE CENTRING TRANSFORM. An animation that sets `transform` replaces
   the rule's transform for its whole duration, so a keyframe of `translateY(10px)` alone would
   drop the `translateX(-50%)` and the panel would snap left for 180ms and then jump into place.
   Steel's own `jp-in` writes `translateX(-50%) translateY(10px)` for exactly this reason. */
@keyframes jp-in-clark {
  from { opacity: 0; transform: translateX(-50%) translateY(10px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.ccv2-clark-jurisdock .jp { animation: jp-in-clark .18s ease; }
@media (prefers-reduced-motion: reduce) { .ccv2-clark-jurisdock .jp { animation: none; } }
/* With the panel centred on the pill, the pointer belongs at the panel's centre — which is now
   the pill's centre — rather than 42px from a left edge that no longer lines up with anything. */
.ccv2-clark-jurisdock .jp::after { left: 50%; transform: translateX(-50%) rotate(45deg); }
/* …and it goes when the panel has been clamped away from the chip (RUN AE · 2). An arrow is a
   claim about what the panel hangs off; once `--jp-drop` is non-zero that claim is false, and the
   ≤720px sheet rule below already makes the same call about the same pseudo-element. */
.ccv2-clark-jurisdock[data-jp-clamped="1"] .jp::after { display: none; }

/* The trigger. Same shape and same filtered tint as Steel's `.st-pill__item--juris`, expressed on
   Clark's own class so neither app's stylesheet reaches into the other's markup. */
.ccv2-clark-jurisbtn {
  display: inline-flex; align-items: center; gap: 7px;
  /* BUNDLE E: canonical roles, no legacy aliases and no raw fallbacks. The chip now sits INSIDE
     the composer's border, so its resting ground is the card it is drawn on rather than a
     separate tinted surface — one border around the unit, not two competing edges. */
  /* 🔴 BUNDLE 1to1 — the mockup's `.pill-j`: gap 7, padding 7px 16px, 13px, and a 1px shadow
     that lifts it off the ground now that it sits ON the ground rather than inside the box. */
  padding: 7px 16px; border-radius: var(--cc-chip-radius);
  border: 1px solid var(--cc-edge);
  background: var(--cc-card);
  box-shadow: 0 1px 2px -1px var(--cc-hairline);
  font: inherit; font-size: 13px; font-weight: 500;
  max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  color: var(--cc-ink); cursor: pointer;
  transition: border-color .15s, color .15s;
}
.ccv2-clark-jurisbtn:hover { border-color: var(--cc-accent-text); }  /* the mockup's `.pill-j:hover` */
.ccv2-clark-jurisbtn.is-filtered {
  background: var(--cc-card-hover); color: var(--cc-accent-text);
  border-color: var(--cc-accent-fill);
}
.ccv2-clark-jurisbtn:focus-visible { outline: 2px solid var(--cc-focus-ring-color); outline-offset: 2px; }
.ccv2-clark-jurisbtn-caret { font-size: 9px; opacity: .7; }

/* The scrim. Steel's picker closes on its pill; Clark's needs a click-away that does not steal
   focus from the composer. Transparent by design — a dimmed thread would read as a modal, and
   this control is explicitly NOT a modal (B6: no prompt, no interstitial). */
.ccv2-clark-jurisscrim { position: fixed; inset: 0; z-index: 59; background: transparent; border: none; padding: 0; }
/* ══ 🔴 STREAM C · PART 6 — A DASHED OUTLINE MEANT THREE DIFFERENT THINGS ════════════════════
   The same dashed border did three unrelated jobs across the product: a LINK (this row), a DROP
   TARGET (the folder a chat is dragged into, in Clark's rail) and a CREATE action (Draft's "Start
   a fresh case" tile). Three meanings, one signal, so the signal means nothing. Only one reading
   is a real convention — dashed = "a shape waiting to be filled" — and it fits the drop target and
   the create tile, not a link. So this one gives it up.

   🔴 AND IT IS NOT RESTYLED AS A LINK EITHER, BECAUSE IT IS `disabled`.
   JurisdictionPicker.jsx renders this as `<button … disabled>` — it navigates nowhere. Dressing a
   dead control as a link would swap one lie for a better-looking one. What it actually is, is a
   HINT about where the setting lives, so it is set as hint text: no border, no pill shape, nothing
   that invites a click it will not answer.
   ⚠️ FOLLOW-UP, FLAGGED: making it a real link into the account panel's Preferences pane is a
   genuinely better answer and it is a small one — the panel already opens from an event. It is not
   done here because wiring a navigation blind is how you ship a second control that goes nowhere. */
.jp__preset--prompt {
  border-color: transparent;
  background: none;
  padding-left: 2px;
  color: var(--cc-text-muted);
  font-style: italic;
  cursor: default;
}
.jp__preset--prompt:hover { border-color: transparent; color: var(--cc-text-muted); }

/* ══ 🔴 STREAM C · 7.4 — THE PICKER AT 390px. POSITIONING ONLY; THE DESIGN IS UNTOUCHED. ══════
   MEASURED FAILURE: the panel's own title row and search field were cut off ABOVE the viewport
   entirely, and the "All jurisdictions / Federal only / Set your home state in settings →" row sat
   partly behind the navy bar. The picker could not be searched at all on a phone.

   THE CAUSE IS ARITHMETIC, NOT DESIGN. The panel is `max-height: min(84vh, 620px)` and grows
   UPWARD from its anchor — `bottom: 84px` in Steel, `bottom: calc(100% + 12px)` off Clark's
   composer. On an 844px viewport 84vh is 709px, and a panel that tall rising from a composer near
   the bottom of the screen has its top edge somewhere around y = -100. Everything above the fold
   is simply not on the screen; nothing clips it, so nothing reports an overflow either.

   THE FIX IS TO PIN BOTH EDGES INSTEAD OF ONE. Below 720px the panel becomes a full-height sheet
   between the nav and the bottom of the screen, so its top can never be negative regardless of how
   tall its contents are, and the COLUMNS scroll inside it rather than the panel growing. Same
   panel, same columns, same chrome, same copy — the brief says do not redesign the picker, and
   nothing here does.

   The pointer arrow is hidden: it points at a control the sheet no longer hangs off. */
@media (max-width: 720px) {
  .jp,
  .ccv2-clark-jurisdock .jp {
    position: fixed;
    /* 8px under the 64px nav — the audit's "partially behind the navy bar", closed by arithmetic
       rather than by a z-index, which would have left it merely on top of the bar. */
    top: 72px;
    bottom: 10px;
    left: 10px;
    right: 10px;
    width: auto;
    max-width: none;
    max-height: none;
    /* The centring transform belongs to a panel positioned by its own left edge at 50%. With both
       edges pinned it would shift the sheet half a screen to the left. */
    transform: none;
    border-radius: 14px;
    /* 🔴🔴 RUN RJ — THE SHEET MUST OUTRANK ITS OWN SCRIM, AND BELOW 720px IT DID NOT. THIS IS THE
       DEFECT ADAM REPORTED, AND IT IS NOT A SCROLL BUG AT ALL.
       `JurisdictionChip.jsx` portals a full-viewport dismiss scrim (`.st-jchip__scrim`, `inset: 0`)
       alongside this sheet, and `chrome.css:296` gives that scrim `z-index: 499` so it out-ranks the
       floating pill (`.st-pill`, 400) that would otherwise paint over it. The base `.jp` rule is
       `z-index: 60`. On DESKTOP that is harmless, because `.jp[data-anchored]` (above) lifts the
       sheet to 500 — one above the scrim — and the anchored path is the desktop path. Below 720px
       `JurisdictionPicker.jsx:117` deliberately sets `place = null` so the CSS sheet rule governs,
       `[data-anchored]` is never written, and the sheet stays at 60 — UNDER its own scrim.
       MEASURED on live production at 390x844, before this rule: a 114-point grid sweep over the
       sheet's interior returned `.st-jchip__scrim` at 114 of 114 points — 100%. Search field, preset
       pills, every checkbox, the list, Clear and Apply: all seven probed controls BLOCKED. So a
       swipe on the list was never delivered to the list, and a tap anywhere on the picker hit a
       dismiss button. That is exactly "looks wrong and does not scroll", and it is why a
       PROGRAMMATIC `scrollTop` moved this list perfectly (0 -> 300) while a finger did nothing:
       assigning scrollTop bypasses hit-testing, and hit-testing was the whole defect.
       ⚠️ THIS IS MEASURED, NOT READ — it reproduces in headless WebKit, unlike every other
       candidate this arc has chased. It is the one thing on this bug that did NOT need Adam's phone.
       500 rather than a bigger number: it is the value the DESKTOP path already uses for this same
       sheet, so both breakpoints now order identically — sheet 500 > scrim 499 > pill 400. Clark
       reaches the same order from the other end (`.ccv2-clark-jurisscrim` is 59, under its sheet's
       60), which is why Clark works on Adam's device and this did not. */
    z-index: 500;
  }
  /* Both keyframes write `translateX(-50%)`, which would re-introduce exactly the shift the rule
     above removes — for the 180ms the animation runs. A sheet slides up from its own edge. */
  .jp, .ccv2-clark-jurisdock .jp { animation: jp-in-sheet .18s ease; }
  @keyframes jp-in-sheet {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  /* Head and foot stay put; the middle scrolls. Without `min-height: 0` a grid child in a flex
     column refuses to shrink below its content and the sheet grows past its own bottom edge. */
  .jp__head, .jp__foot { flex: 0 0 auto; }
  /* 🔴 RUN RJ — THE THREE PROPERTIES CLARK SHIPS AND THIS FILE NEVER GOT. Measured on the SERVED
     bundles at 390x844, not read off the repo: Clark's phone rule is
     `{flex:1 1 auto;min-height:0;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;touch-action:pan-y}`
     and Research's is the first three declarations only. That is the whole of the drift in this
     rule, and Adam's device says the surface carrying the extra three is the one that scrolls.
     `overscroll-behavior: contain` (RUN CK's finding, ported): `auto` lets a touch that begins in
     this list hand itself to whatever is behind it the moment the list reaches either end, and on
     iOS that hand-off can take the WHOLE gesture, not just its tail — which is the symptom Adam
     reported on Clark and reports here.
     `touch-action: pan-y` (RUN FX): this element scrolls on one axis and no other, so declaring
     the axis can only ever match what `auto` already resolves to — it cannot change behaviour
     where things work, and it removes any question of an ancestor's `touch-action` being computed
     differently by another engine.
     `-webkit-overflow-scrolling: touch` for iOS versions old enough to need momentum opted into
     on a nested scroller.
     ⚠️ READ, NOT MEASURED, FOR iOS — no headless engine reproduces the defect (a WebKit touch
     swipe scrolls this list correctly BEFORE the change). These are ported because the surface
     that has them works on Adam's phone and the one without them does not; that is the strongest
     evidence available without the device. */
  .jp__cols {
    flex: 1 1 auto; min-height: 0; overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
  }
  /* RUN VF — THE FEDERAL LIST USES THE WIDTH THE STACK GAVE IT. `.jp__cols` above stacks federal
     over state (one column, full sheet width), which is right for the STATE grid but wrong for
     federal: the desktop layout puts this list in a 244px sidebar, one circuit per row, and that
     row width shipped unchanged into a ~340-394px sheet — a checkbox and "Eleventh Circuit" with
     150+px of blank space beside every row, thirteen circuits deep before Specialty courts even
     starts. Same fix already proven one column over: `.jp__grid` (below) grids the state list;
     this does the same to federal, and needs no JSX change, because every top-level federal entry
     is already exactly one grid-auto-placed child — the bare Supreme Court `<Row>`, each circuit's
     `<div key={c.id}>` (which carries its own expanded district sub-list, so an expanded circuit's
     cell simply grows taller than its neighbour, the same way an expanded state cell already does
     in `.jp__grid`), and each specialty court's bare `<Row>`. Only the two `<h4>` eyebrows need to
     span both columns, or "Federal" and "Specialty courts" would land mid-row over a column edge. */
  /* 🔴 RUN RJ — AND IT IS WITHDRAWN, ON THE EVIDENCE ADAM SUPPLIED. VF's reasoning above is sound
     about the WASTED WIDTH and it is why this was tried; what it could not weigh is the comparison
     that now exists — Clark's single-column federal list on the same phone, which Adam reports
     looks correct, against this one, which he reports looks wrong.
     MEASURED at 390x844, this rule gives each federal cell 161px, and 161px does not fit the
     labels: "Second Circuit", "Fourth Circuit", "Seventh Circuit" and "Eleventh Circuit" all WRAP
     to two lines, so the list renders as ragged pairs of unequal height with the disclosure carets
     no longer aligned to anything. It also strands "Federal Circuit" beside "D.C. Circuit" in a
     half-empty final row. Trading 150px of clean whitespace for four wrapped labels and a ragged
     rhythm is a bad trade, and it is the single most visible thing separating the two sheets.
     The 44px floors that VF added in the same block are NOT lost with it — they move to hit
     rectangles below, which buy the same touch target without the row height. */
  /* 🔴🔴 RUN RJ — THE CLIP COMES OFF THE `position: fixed` ANCESTOR, AND THE FOOT DRAWS THE CORNER
     THE CLIP USED TO. This is RUN FX's Clark change, ported, and it is the ONE STRUCTURAL
     difference left between the two sheets once the portal is accounted for.
     UF-1's note above is still true about the CORNERS and it is why this rule existed. What it did
     not weigh is the shape it leaves behind: nested scrolling inside `position: fixed` +
     `overflow: hidden` is a long-standing iOS Safari weak spot, and `.jp` is exactly that shape
     wrapping `.jp__cols`. MEASURED on the served bundles at 390x844 — Clark `.jp` computes
     `overflow: visible`, Research `.jp` computes `overflow: hidden`, and everything else in the
     ancestor chain is identical, both sheets already being direct children of `<body>`.
     UF-1 itself established on the DESKTOP copy that this rule never contained the scroll
     (`.jp__cols` carries its own `overflow: auto`), so nothing is un-contained by removing it —
     only the corners were ever at stake, and the next rule takes those back.
     13px is not a fitted number: `.jp` is `border-radius: 14px` (above) on its border box with a
     `1px` border, so 13px IS the inner curve the clip was drawing. Clark measured the alternatives
     against a same-state control and 13px came in at max channel delta 6, below the 14 levels
     VG-3 accepted as visually identical.
     ⚠️ The pointer is `display: none` at this breakpoint (next rule), so un-clipping cannot bring
     back the notch UF-1 found on desktop. */
  .jp, .ccv2-clark-jurisdock .jp { overflow: visible; }
  .jp__foot { border-radius: 0 0 13px 13px; }
  /* The arrow pointed at the pill the panel used to hang off. */
  .jp::after, .ccv2-clark-jurisdock .jp::after { display: none; }

  /* RUN VF — the 44px floor, on the controls a reader has to tap to leave this sheet or scope it.
     Measured before: `.jp__apply` 85.8×32, `.jp__clear` 30.7×15, both well under the STREAM C 7.8
     floor this codebase already applies to the pill's own buttons two files over. `min-height`/
     `min-width` only (never new padding that would change the desktop density) — the checkbox rows
     below stay at the desktop's compact size on purpose: this is a scrollable multi-select tree of
     ~300 rows, not a five-item strip, and a 44px floor on every row would turn a several-screen
     scroll into a dozen. `justify-content: center` on Clear keeps its underlined text centred in
     the taller box rather than pinned to its old 15px top. */
  /* 🔴 RUN RJ — THE 44px FLOOR, KEPT, BUT AS A HIT RECTANGLE INSTEAD OF A BOX HEIGHT.
     VF was right that these controls were under the floor and it measured them honestly
     (`.jp__apply` 85.8x32, `.jp__clear` 30.7x15). What `min-height` does, though, is grow the
     PAINTED box, and that is what put Research visibly out of step with Clark: preset pills at 44px
     against Clark's 28px, a 61px footer against Clark's 56px, and — worst — a 44px floor on
     `.jp__exp`, which sits INSIDE every list row and so inflated each row from 19px to 47px.
     That is the very outcome VF's own note said it was avoiding ("a 44px floor on every row would
     turn a several-screen scroll into a dozen"): measured, this list's scrollHeight was 1876px
     against Clark's 1067px, for identical content.
     The codebase already has the right shape for this — `jurispicker.css`'s own
     `.ccv2-clark-jurisbtn::after` (RUN FU · STEP 3) — a centred, absolutely-positioned pseudo
     element that enlarges what a finger can hit WITHOUT touching layout. Applied here, every
     control keeps a >=44px target and every painted box returns to Clark's dimensions.
     ⚠️ `::after` is free on all four: none of these elements uses one (the sheet's pointer is
     `.jp::after`, a different element), and each gets `position: relative` so the rectangle is
     measured from the control and not from the sheet. */
  .jp__apply, .jp__clear, .jp__preset, .jp__exp { position: relative; }
  .jp__apply::after, .jp__clear::after, .jp__preset::after, .jp__exp::after {
    content: ""; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
    width: 100%; height: 100%; min-width: 44px; min-height: 44px;
  }
}
@media (max-width: 720px) and (prefers-reduced-motion: reduce) {
  .jp, .ccv2-clark-jurisdock .jp { animation: none; }
}


/* ══ CLARK-ONLY — the jurisdiction button under the composer. Inert in Research. ════════════════ */
@media (max-width: 768px) {
  .ccv2-clark-jurisbtn { position: relative; margin-block: 10px; }
  .ccv2-clark-jurisbtn::after { content: ""; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 100%; min-width: 44px; height: 44px; }
}

@media (max-width: 768px) {
  /* 🔴 RUN FU · STEP 3 (batch 6) — near misses. Each of these measured 37–43px of HIT RECTANGLE
   against a 44px bar. 46px, not 44: the hit-test marches whole pixels from the control's centre and
   therefore under-reports a true 44px target by up to two, so 46 is the bar plus the instrument's
   own pessimism — stated rather than buried in a tolerance. Nothing above 768px is touched. */
  .ccv2-clark-jurisbtn { min-height: 46px; }
}
